import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { login as apiLogin, resendVerification, type RequiresTwoFactor, type User } from '../api/auth'
import { useAuth } from '../context/AuthContext'
import TwoFactorCodeStep from '../components/TwoFactorCodeStep'
import { Coin } from '../icons/Coin.tsx'
import { rememberReturn, authUrl, referrerPath } from '../utils/authReturn'
import { useAuthNav } from '../hooks/useAuthModal'
import { useWelcomeCredits } from '../hooks/useWelcomeCredits'
import TurnstileWidget, { type TurnstileWidgetHandle } from '../components/TurnstileWidget'
import { useTurnstile } from '../hooks/useTurnstile'

const TG_BOT_ID = '8464718685'

type ApiErrorLike = Error & { captchaRequired?: boolean }

export default function Login() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [needsVerification, setNeedsVerification] = useState(false)
  const [twoFA, setTwoFA] = useState<RequiresTwoFactor | null>(null)
  const [resending, setResending] = useState(false)
  const [resendMsg, setResendMsg] = useState('')
  // Логин — адаптивно: виджет появляется только после того, как бэк по
  // заголовку X-Captcha-Required сказал, что счётчик неудач достиг порога.
  const [captchaRequired, setCaptchaRequired] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const loginWidgetRef = useRef<TurnstileWidgetHandle>(null)
  // Ресенд письма — всегда (TZ_turnstile_captcha.md, 3.5), отдельный токен
  // и отдельный виджет: рендерится сразу, как только появляется блок ресенда.
  const [resendCaptchaToken, setResendCaptchaToken] = useState('')
  const resendWidgetRef = useRef<TurnstileWidgetHandle>(null)
  const welcomeCredits = useWelcomeCredits()
  const { enabled: captchaEnabled } = useTurnstile()
  // Вход в окне поверх страницы закрывает окно, на полной странице — уводит на запомненный адрес (useAuthNav)
  const authNav = useAuthNav()
  const [params] = useSearchParams()
  const { login } = useAuth()

  // Запоминаем «откуда пришёл» (?next) — вернёмся туда после успешного входа.
  useEffect(() => {
    rememberReturn(params.get('next') || referrerPath())
  }, [params])

  const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
  const isEmailLatin = /^[a-zA-Z0-9._%+@-]*$/.test(email)

  const getEmailState = () => {
    if (!touched.email || !email) return ''
    if (!isEmailLatin) return 'error'
    if (isEmailValid) return 'valid'
    if (email.length > 3) return 'error'
    return ''
  }

  const getEmailError = () => {
    if (!touched.email || !email) return ''
    if (!isEmailLatin) return t('auth.error.emailLatin')
    if (!isEmailValid && email.length > 3) return t('auth.error.emailInvalid')
    return ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setNeedsVerification(false)
    setResendMsg('')
    setTouched({ email: true })

    if (!isEmailValid) {
      setError(!isEmailLatin ? t('auth.error.emailLatinShort') : t('auth.error.emailInvalidShort'))
      return
    }

    if (!password) {
      setError(t('auth.error.enterPassword'))
      return
    }

    if (captchaRequired && !captchaToken) {
      setError(t('auth.captcha.required'))
      return
    }

    setLoading(true)
    try {
      const result = await apiLogin(email, password, captchaToken || undefined)
      if ('requires_2fa' in result) {
        setTwoFA(result)
        return
      }
      login(result)
      authNav.done()
    } catch (err) {
      const apiErr = err as ApiErrorLike
      const msg = err instanceof Error ? err.message : t('auth.error.loginFailed')
      setError(msg)
      if (msg === 'Email не подтверждён') setNeedsVerification(true)
      // Счётчик неудач достиг порога именно этой попыткой — на следующей
      // капча обязательна. detail при этом не менялся (не-энумерация).
      if (apiErr?.captchaRequired) setCaptchaRequired(true)
      // Токен одноразовый — сбрасываем после ЛЮБОЙ неудачи, иначе повтор
      // словит timeout-or-duplicate.
      loginWidgetRef.current?.reset()
      setCaptchaToken('')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    setResendMsg('')
    try {
      await resendVerification(email, resendCaptchaToken || undefined)
      setResendMsg(t('auth.resend.sent'))
    } catch (err) {
      const apiErr = err as ApiErrorLike
      setResendMsg(apiErr?.captchaRequired ? t('auth.captcha.failed') : t('auth.resend.error'))
    } finally {
      resendWidgetRef.current?.reset()
      setResendCaptchaToken('')
      setResending(false)
    }
  }

  const handleTelegram = () => {
    const origin = encodeURIComponent(window.location.origin)
    const returnTo = encodeURIComponent('https://umnik.ai/auth/telegram-callback')
    window.location.href = `https://oauth.telegram.org/auth?bot_id=${TG_BOT_ID}&origin=${origin}&embed=0&request_access=write&return_to=${returnTo}`
  }

  const emailState = getEmailState()

  const handleTwoFASuccess = (user: User) => {
    login(user)
    authNav.done()
  }

  if (twoFA) {
    return (
      <div className="auth-page">
        <div className="auth-card-frame gradient-frame">
          <div className="auth-card">
            <h1 className="auth-title">{t('auth.login.title')}</h1>
            <TwoFactorCodeStep challenge={twoFA} onSuccess={handleTwoFASuccess} onCancel={() => setTwoFA(null)} />
          </div>
        </div>
      </div>
    )
  }

  return (
      <div className="auth-page">
        <div className="auth-card-frame gradient-frame">
          <div className="auth-card">
          {/* Бонус выключен (0) или ещё грузится (null) — бейджа нет */}
          {!!welcomeCredits && (
            <span className="auth-card__badge">
              {welcomeCredits} <Coin className="auth-card__icon" /> {t('auth.registrationBonus')}
            </span>
          )}

            <h1 className="auth-title">{t('auth.login.title')}</h1>
            <p className="auth-subtitle">{t('auth.login.subtitle')}</p>

            {error && <div className="auth-error">{error}</div>}

            {needsVerification && (
              <div className="auth-resend">
                <span className="auth-resend__prompt">{t('auth.resend.prompt')}</span>
                <TurnstileWidget
                  ref={resendWidgetRef}
                  action="resend_verification"
                  onToken={setResendCaptchaToken}
                  onExpire={() => setResendCaptchaToken('')}
                />
                <button
                  type="button"
                  className="auth-btn auth-btn-secondary"
                  onClick={handleResend}
                  disabled={resending || (captchaEnabled && !resendCaptchaToken)}
                >
                  {resending ? t('auth.resend.sending') : t('auth.resend.button')}
                </button>
                {resendMsg && <div className="auth-resend__msg">{resendMsg}</div>}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <div className="auth-input-wrapper">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    placeholder="E-mail"
                    className={`auth-input ${emailState}`}
                />
                {emailState === 'valid' && (
                    <img
                        src="/icons/check.svg"
                        className="auth-input-icon"
                        alt=""
                        width="18"
                        height="16"
                    />
                )}
                {emailState === 'error' && (
                    <img
                        src="/icons/cross.svg"
                        className="auth-input-icon"
                        alt=""
                        width="10"
                        height="10"
                    />
                )}
              </div>
              {getEmailError() && <div className="auth-field-error">{getEmailError()}</div>}

              <div className="auth-input-wrapper">
                <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.password')}
                    className="auth-input"
                />
                {password.length > 0 && (
                    <button
                        type="button"
                        className="auth-eye-btn"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                      <img
                          src={showPassword ? '/icons/visible.svg' : '/icons/notvisible.svg'}
                          alt=""
                          width="15"
                      />
                    </button>
                )}
              </div>

              <div className="auth-forgot">
                <Link to="/forgot-password" {...authNav.link}>{t('auth.forgotPassword')}</Link>
              </div>

              {/* Адаптивно: виджета не видно, пока бэк не потребовал капчу
                  заголовком X-Captcha-Required (см. handleSubmit). */}
              {captchaRequired && (
                <TurnstileWidget
                  ref={loginWidgetRef}
                  action="login"
                  onToken={setCaptchaToken}
                  onExpire={() => setCaptchaToken('')}
                />
              )}

              <button
                type="submit"
                className="auth-btn auth-btn-primary"
                disabled={loading || (captchaRequired && !captchaToken)}
              >
                {loading ? t('auth.login.loading') : t('auth.login.submit')}
              </button>
            </form>

            <button className="auth-btn auth-btn-telegram" onClick={handleTelegram}>
              {t('auth.telegramLogin')}
              <img src="/icons/telegram.svg" alt="" width="20" height="20" />
            </button>

            <Link to={authUrl('/register', params.get('next'))} {...authNav.link} className="auth-link">
              {t('auth.register.link')}
            </Link>
          </div>
        </div>
      </div>
  )
}
